// Create the Redux Store and the Browser History
import { createBrowserHistory, type Location } from "history";
import {
    type AnyAction,
    applyMiddleware,
    createStore,
    type PreloadedState,
    type Store,
} from "redux";
import { composeWithDevTools } from "redux-devtools-extension/developmentOnly";
import { thunk as thunkMiddleware } from "redux-thunk";
import reducers, { type RootState } from "../Reducers/ShopfrontReducers";
import { fireEventType } from "../Utilities/Generic/ActionsHelper";
import { applicationBridgeMiddleware } from "../Utilities/Platform/Applications/ReduxApplicationBridge";
import { DISABLE_EMBEDDED, EMBEDDED_PARAM_NAME, hasIntegrationSetting } from "../Utilities/Platform/CurrentRequest";
import { LOCATION_CHANGE } from "./GlobalConstants";
import type { ShopfrontDispatch } from "./hooks.ts";

export type LocationWithState<T> = Location & Partial<{
    state: undefined | T;
}>;

export interface LocationChangeAction {
    type: typeof LOCATION_CHANGE;
}

// Create our router history
const browserHistory = createBrowserHistory();

// Prepare our Redux Store
const composeEnhancers = composeWithDevTools({
    trace: process.env.NODE_ENV !== "production",
});

export type ShopfrontStore = Store<RootState, AnyAction> & {
    dispatch: ShopfrontDispatch;
};

/**
 * Creates a shopfront redux store
 * @param preloadedState
 */
export function createShopfrontStore(preloadedState?: PreloadedState<RootState>): ShopfrontStore {
    return createStore<RootState, AnyAction, {}, {}>(
        reducers(),
        preloadedState,
        composeEnhancers(
            applyMiddleware(
                thunkMiddleware,
                applicationBridgeMiddleware
            )
        )
    );
}

const tempStore = createShopfrontStore();

// This handles ensuring that the sf_embedded is re-added to every location change
browserHistory.listen(({ location }) => {
    if(!hasIntegrationSetting || !DISABLE_EMBEDDED) {
        tempStore.dispatch(fireEventType(LOCATION_CHANGE));
    } else {
        const searchParams = new URLSearchParams(location.search);

        if(searchParams.has(EMBEDDED_PARAM_NAME)) {
            tempStore.dispatch(fireEventType(LOCATION_CHANGE));
        } else {
            searchParams.set(EMBEDDED_PARAM_NAME, "false");

            browserHistory.replace(`${location.pathname}?${searchParams}${location.hash}`, location.state);
        }
    }
});

const tempHistory = browserHistory;

export const store = tempStore;
export const history = tempHistory;
